import type { InspectionStatus } from "@/hera-api/client/types.gen";
import { ALL_INSPECTION_STATUSES } from "@/lib/enum-constants";
import { getInspectionStatusDisplay } from "@/lib/utils";
import type { StatusBadgeVariant } from "@/lib/utils";

/** Display entry for the inspection status info panel: status key, label, and badge variant. */
export interface InspectionStatusDisplayItem {
  id: string;
  label: string;
  variant: StatusBadgeVariant;
}

/** All inspection statuses and the special Awaiting Check display, for the info panel. */
export function getInspectionStatusDisplayList(): InspectionStatusDisplayItem[] {
  const items: InspectionStatusDisplayItem[] = [];
  const seen = new Set<string>();

  for (const status of ALL_INSPECTION_STATUSES) {
    const { label, variant } = getInspectionStatusDisplay(status);
    const key = `${status}:${label}`;
    if (seen.has(key)) continue;
    seen.add(key);
    items.push({ id: status, label, variant });
  }

  // Add "Awaiting Check" (shown when REQUIRES_PAYMENT/PAYMENT_FAILED + check pending)
  const awaiting = getInspectionStatusDisplay("REQUIRES_PAYMENT", true);
  items.push({
    id: "AWAITING_CHECK_DISPLAY",
    label: awaiting.label,
    variant: awaiting.variant,
  });

  return items;
}

/**
 * Possible transitions between inspection statuses (from -> to).
 * Derived from backend behavior: payment flow, license approval, and inspection outcome.
 */
export const INSPECTION_STATUS_TRANSITIONS: readonly (readonly [
  InspectionStatus,
  InspectionStatus,
])[] = [
  ["REQUIRES_PAYMENT", "PAYMENT_PROCESSING"],
  ["PAYMENT_FAILED", "PAYMENT_PROCESSING"],
  ["PAYMENT_FAILED", "REQUIRES_PAYMENT"],
  ["PAYMENT_PROCESSING", "PAID_PENDING_REVIEW"],
  ["PAYMENT_PROCESSING", "PAYMENT_FAILED"],
  ["PAID_PENDING_REVIEW", "INSPECTION_PENDING"],
  ["PAID_PENDING_REVIEW", "CANCELLED"],
  ["INSPECTION_PENDING", "COMMITTED"],
  ["COMMITTED", "COMPLIANT"],
  ["COMMITTED", "INSPECTION_PENDING"],
  ["COMMITTED", "NON_COMPLIANT"],
  ["INSPECTION_PENDING", "NON_COMPLIANT"],
  ["INSPECTION_PENDING", "CANCELLED"],
  ["INSPECTION_PENDING", "EXEMPT"],
] as const;

/**
 * Flow diagram: payment-method paths and subsequent inspection flow.
 * Each step is a display label key (status or "AWAITING_CHECK" for the Awaiting Check display).
 */
export type FlowStepKey =
  | "REQUIRES_PAYMENT"
  | "PAYMENT_PROCESSING"
  | "PAYMENT_FAILED"
  | "AWAITING_CHECK"
  | "PAID_PENDING_REVIEW"
  | "INSPECTION_PENDING"
  | "COMMITTED"
  | "COMPLIANT"
  | "NON_COMPLIANT"
  | "CANCELLED"
  | "EXEMPT";

/** One edge in the flow diagram: from status, what triggers it, to status. */
export interface FlowEdge {
  from: FlowStepKey;
  trigger: string;
  to: FlowStepKey;
}

/**
 * All edges for the inspection status flow diagram. Group by `from` to show
 * one section per status with multiple outgoing transitions (each labeled by trigger).
 */
export const INSPECTION_FLOW_EDGES: FlowEdge[] = [
  { from: "REQUIRES_PAYMENT", trigger: "ACH", to: "PAYMENT_PROCESSING" },
  {
    from: "REQUIRES_PAYMENT",
    trigger: "Credit Card",
    to: "PAID_PENDING_REVIEW",
  },
  { from: "REQUIRES_PAYMENT", trigger: "Check", to: "AWAITING_CHECK" },
  { from: "PAYMENT_PROCESSING", trigger: "", to: "PAID_PENDING_REVIEW" },
  { from: "PAYMENT_PROCESSING", trigger: "", to: "PAYMENT_FAILED" },
  { from: "AWAITING_CHECK", trigger: "", to: "PAID_PENDING_REVIEW" },
  { from: "PAID_PENDING_REVIEW", trigger: "", to: "INSPECTION_PENDING" },
  { from: "PAID_PENDING_REVIEW", trigger: "", to: "CANCELLED" },
  { from: "PAID_PENDING_REVIEW", trigger: "", to: "EXEMPT" },
  { from: "INSPECTION_PENDING", trigger: "Submit", to: "COMMITTED" },
  { from: "COMMITTED", trigger: "Approve", to: "COMPLIANT" },
  { from: "COMMITTED", trigger: "Reject", to: "INSPECTION_PENDING" },
  { from: "INSPECTION_PENDING", trigger: "", to: "NON_COMPLIANT" },
  { from: "INSPECTION_PENDING", trigger: "", to: "CANCELLED" },
];

/** Order of nodes for the diagram (so we show e.g. Balance Due first). */
export const INSPECTION_FLOW_NODE_ORDER: FlowStepKey[] = [
  "REQUIRES_PAYMENT",
  "PAYMENT_PROCESSING",
  "PAYMENT_FAILED",
  "AWAITING_CHECK",
  "PAID_PENDING_REVIEW",
  "INSPECTION_PENDING",
  "COMMITTED",
  "COMPLIANT",
  "NON_COMPLIANT",
  "CANCELLED",
  "EXEMPT",
];

/** Grid position for flowchart: row and column (0-based). */
export interface FlowNodePosition {
  row: number;
  col: number;
}

/**
 * Flowchart layout: left-to-right flow. (row, col) with row = vertical, col = horizontal.
 * Balance Due far left; payment column has Awaiting Check, then gap, then Payment Processing and Payment Failed
 * lower with space between them so the connecting line has visible length.
 */
export const INSPECTION_FLOW_LAYOUT: Record<FlowStepKey, FlowNodePosition> = {
  REQUIRES_PAYMENT: { row: 1, col: 0 },
  PAYMENT_PROCESSING: { row: 4, col: 0 },
  PAYMENT_FAILED: { row: 6, col: 0 },
  AWAITING_CHECK: { row: 2, col: 1 },
  PAID_PENDING_REVIEW: { row: 1, col: 2 },
  INSPECTION_PENDING: { row: 1, col: 3 },
  COMMITTED: { row: 1, col: 4 },
  COMPLIANT: { row: 1, col: 5 },
  NON_COMPLIANT: { row: 2, col: 5 },
  CANCELLED: { row: 3, col: 5 },
  EXEMPT: { row: 4, col: 5 },
};
